// src/App.js

import './App.css';
import { Auth } from './components/auth/Auth';
import { FileUpload } from './components/dashboard/FileUpload';
import { FileList } from './components/dashboard/FileList';
import { Dashboard } from './components/dashboard/Dashboard';
import { Navbar } from './components/dashboard/Navbar';
import { ChatModal } from './components/modals/ChatModal';
import { FileSelectorModal } from './components/modals/FileSelectorModal';
import { SettingsPage } from './components/modals/SettingsPage';
import { SharedFileViewer } from './components/viewer/SharedFileViewer';
import { ResetPassword } from './components/auth/ResetPassword';
import { supabase } from './supabase.js';
import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';

const MainLayout = ({ user }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [isFileSelectorOpen, setIsFileSelectorOpen] = useState(false);
  const navigate = useNavigate();

  const [files, setFiles] = useState([]);
  const [storageUsed, setStorageUsed] = useState(0);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);

  // Fetch all files from Supabase
  const fetchFiles = React.useCallback(async () => {
    if (user) {
      // Only show full loading screen if we have no files yet
      setFiles(prev => {
        if (prev.length === 0) setIsLoadingFiles(true);
        return prev;
      });
      
      try {
        const { data, error } = await supabase
          .from('files')
          .select('*')
          .eq('uid', user.id)
          .order('uploadedAt', { ascending: false });

        if (error) {
          console.error("Error fetching files:", error);
        } else {
          let totalSize = 0;
          const fetchedFiles = (data || []).map(doc => {
              totalSize += doc.size || 0;
              return { id: doc.id, ...doc };
          });
          
          setFiles(fetchedFiles);
          setStorageUsed(totalSize);
        }
      } catch (err) {
        console.error("Fetch files unexpected error:", err);
      } finally {
        setIsLoadingFiles(false);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchFiles();
    
    // Set up realtime listener for the files table (requires Realtime enabled in Supabase, but degrades gracefully)
    const channel = supabase
      .channel('public:files')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'files', filter: `uid=eq.${user.id}` }, (payload) => {
         fetchFiles(); // Re-fetch on any change
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, fetchFiles]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      console.error(err);
    }
  };

  const openGlobalChat = (file = null) => {
    setAttachedFile(file);
    setIsChatModalOpen(true);
  };
  
  const handleFileSelect = (file) => {
    setAttachedFile(file);
  };

  if (!user) {
    return <Navigate to="/login" />;
  }
  
  return (
    <>
      <Navbar user={user} onLogout={handleLogout} onOpenChat={openGlobalChat} onOpenSettings={() => setIsSettingsOpen(true)} storageUsed={storageUsed} />
      
      <main className="main-content">
        <Routes>
          <Route path="/" element={
            <div className="dashboard-grid">
              <div className="dashboard-sidebar">
                <FileUpload onUploadSuccess={fetchFiles} user={user} />
                <Dashboard files={files} />
              </div>
              <div className="dashboard-content">
                <div className="search-container">
                  <input 
                    type="text"
                    placeholder="Search your files by name or tag..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <FileList files={files} isLoading={isLoadingFiles} searchTerm={searchTerm} onOpenFileChat={openGlobalChat} onRefresh={fetchFiles} user={user} />
              </div>
            </div>
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      
      <ChatModal isOpen={isChatModalOpen} onRequestClose={() => setIsChatModalOpen(false)} file={attachedFile} onAttachFileClick={() => setIsFileSelectorOpen(true)} onRemoveFileClick={() => setAttachedFile(null)} />
      <FileSelectorModal isOpen={isFileSelectorOpen} onRequestClose={() => setIsFileSelectorOpen(false)} onFileSelect={handleFileSelect} />
      <SettingsPage isOpen={isSettingsOpen} onRequestClose={() => setIsSettingsOpen(false)} />
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let hasHandledConfirmation = false;

    const handlePendingVerification = async () => {
      if (hasHandledConfirmation) return;
      hasHandledConfirmation = true;
      localStorage.removeItem('mydrive_pending_verification');
      await supabase.auth.signOut();
      setUser(null);
      // Clean any auth tokens/codes from the URL
      window.history.replaceState({}, document.title, window.location.pathname);
      toast.success("Email verified successfully! Please sign in with your credentials.");
    };

    const isPendingVerification = () => {
      if (hasHandledConfirmation) return false;
      return localStorage.getItem('mydrive_pending_verification') === 'true';
    };

    // Check active sessions on load
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user && isPendingVerification()) {
        // User clicked confirmation link — sign them out, force manual login
        await handlePendingVerification();
      } else if (!hasHandledConfirmation) {
        setUser(session?.user ?? null);
      }
      setLoading(false);
    });

    // Listen for auth state changes (fires when Supabase exchanges confirmation code)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (isPendingVerification() && (event === 'SIGNED_IN' || session?.user)) {
        // Confirmation link just created a session — block it
        await handlePendingVerification();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      } else if (!hasHandledConfirmation) {
        setUser(session?.user ?? null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (loading) {
    return <p>Loading Application...</p>;
  }

  return (
    <ThemeProvider>
      <Router>
        <Toaster 
          position="top-center" 
          reverseOrder={false} 
          toastOptions={{
            duration: 2500,
            style: {
              background: 'var(--color-surface)',
              backdropFilter: 'var(--prism-blur)',
              WebkitBackdropFilter: 'var(--prism-blur)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
              borderRadius: '16px',
              fontFamily: 'var(--font-body)',
              fontSize: '14px',
              fontWeight: '600',
              padding: '12px 24px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
            },
            success: {
              duration: 2500,
              iconTheme: {
                primary: 'var(--color-success)',
                secondary: 'var(--color-background)',
              },
            },
            error: {
              duration: 2500,
              iconTheme: {
                primary: 'var(--color-danger)',
                secondary: 'var(--color-background)',
              },
            },
            loading: {
              iconTheme: {
                primary: 'var(--color-primary)',
                secondary: 'var(--color-background)',
              },
            },
          }}
        />
        <div className="App">
          <Routes>
            <Route path="/login" element={user ? <Navigate to="/" /> : <Auth />} />
            {/* Password Reset landing page */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public route — anyone with the link can view */}
            <Route path="/share/:linkId" element={<SharedFileViewer />} />
            <Route path="/*" element={user ? <MainLayout user={user} /> : <Navigate to="/login" />} />
          </Routes>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;